import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { weekDays } from '@acceligentllc/shared/enums/weekDay';

import type { OrganizationNotificationType } from 'ab-enums/organizationNotificationType.enum';

import type * as ReduxFormKeys from 'af-constants/reduxForms';

import NotificationTimeSettingsForm from 'af-components/NotificationTimeSettingsForm/NotificationTimeSettingsForm';

import * as OrganizationsettingsActions from 'af-actions/organizationSettings';

import type { RegistrationAndDeactivationNotificationTimeSettingsFM } from './formModel';

interface TimeSettings {
	notificationTime: string;
	timezone: string;
	notificationDays: { id: number; label: string; }[];
}

type ReduxKeyType = typeof ReduxFormKeys[keyof typeof ReduxFormKeys];

type OwnProps = {
	notificationType: OrganizationNotificationType;
	formType: ReduxKeyType;
};

type Props = ConnectedProps<typeof connector> & OwnProps;

const NotificationTimeSettings: React.FC<Props> = (props) => {

	const [timeSettings, setTimeSettings] = React.useState<Nullable<TimeSettings>>(null);

	const {
		findOrganizationNotificationTimeSettings,
		updateOrganizationNotificationTimeSettings,
		notificationType,
		formType,
	} = props;

	const fetchTimeSettings = React.useCallback(async () => {
		const _timeSettings = await findOrganizationNotificationTimeSettings(notificationType);

		setTimeSettings({
			..._timeSettings,
			notificationDays: _timeSettings.notificationDays.map((_id) => ({ id: _id, label: weekDays[_id - 1] })),
		});
	}, [findOrganizationNotificationTimeSettings, notificationType]);

	React.useEffect(() => {
		fetchTimeSettings();
	}, [fetchTimeSettings]);

	const updateTimeSettings = React.useCallback(async (form: RegistrationAndDeactivationNotificationTimeSettingsFM) => {
		await updateOrganizationNotificationTimeSettings(
			notificationType,
			{ ...form, notificationDays: form?.notificationDays.map((i) => i.id)}
		);
		await fetchTimeSettings();
	}, [fetchTimeSettings, notificationType, updateOrganizationNotificationTimeSettings]);

	return (
		<NotificationTimeSettingsForm
			form={formType}
			initialValues={timeSettings ?? undefined}
			onSubmit={updateTimeSettings}
			showDaySelector={true}
		/>
	);
};

function mapDispatchToProps() {
	return {
		findOrganizationNotificationTimeSettings: OrganizationsettingsActions.findOrganizationNotificationTimeSettings,
		updateOrganizationNotificationTimeSettings: OrganizationsettingsActions.updateOrganizationNotificationTimeSettings,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(NotificationTimeSettings);
